import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import {
    isDisplayCustomerTicketList,
    isDisplayHelpdeskTicketList,
    isDisplayKnowledgeMenu
} from '@/component/common/layout/gNav/handler/MenuHandler';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { handleUpdateProjectResource } from '@/service/projectService';
import { getPublicAPI } from '@/service/publicService';
import I18n from '@/rI18n';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

const sortAndAssignOrder = (items, orderField = 'order', createdAtField = 'createdAt') => {
    if (!Array.isArray(items) || items.length === 0) {
        return items;
    }

    // Sort: items with order sorted by order, items without order
    const sortedItems = [...items].sort((a, b) => {
        const hasOrderA = typeof a[orderField] === 'number' && !isNaN(a[orderField]);
        const hasOrderB = typeof b[orderField] === 'number' && !isNaN(b[orderField]);
        
        if (hasOrderA && hasOrderB) {
            return a[orderField] - b[orderField];
        } else if (hasOrderA) {
            return -1;
        } else if (hasOrderB) {
            return 1;
        } else {
            // If both items don't have order, sort by createdAt
            const dateA = a[createdAtField] ? new Date(a[createdAtField]) : new Date(0);
            const dateB = b[createdAtField] ? new Date(b[createdAtField]) : new Date(0);
            return dateA - dateB;
        }
    });

    // Calculate max order value from existing items
    let currentMaxOrder = Math.max(
        0,
        ...sortedItems.map(item => 
            (typeof item[orderField] === 'number' && !isNaN(item[orderField])) 
                ? item[orderField] 
                : 0
        )
    );

    // Assign order to items that don't have order
    return sortedItems.map(item => {
        if (item[orderField] == null || (typeof item[orderField] !== 'number' || isNaN(item[orderField]))) {
            currentMaxOrder += 1;
            return { ...item, [orderField]: currentMaxOrder };
        }
        return item;
    });
};

const buildBusinessAppsByProjectInuseSetting = (businessApps, settingActivePanels) => {
    businessApps = businessApps
        .map(app => {
            const findApp = settingActivePanels.find(settingApp => settingApp.id === app.id);
            if (!findApp) return {
                ...app,
                order: null // reset order of app not in settingActivePanels
            };

            return {
                ...app,
                active: findApp.active,
                order: findApp.order,
                checkedAt: findApp.checkedAt
            };
        });

    businessApps = sortAndAssignOrder(businessApps); // sort and assign order of apps

    return businessApps;
}

/**
 * Build base panel menu item.
 * `name` is the source (Japanese) text, never a translated one: it is kept in state,
 * so the component translates it with tr() at render time and follows the
 * translation dictionary as it loads.
 */
const buildBasePanelItem = (panelType, name, order, defaultActive = true) => {
    return {
        id: panelType,
        name,
        order,
        active: defaultActive,
        checkedAt: null
    };
};

/**
 * Get setting panel from activePanels
 */
const getSettingPanel = (settingActivePanels, panelType) => {
    if (!settingActivePanels) return null;
    return settingActivePanels.find(panel => panel.id === panelType);
};

/**
 * Build QA panel menu
 */
const buildQAPanel = (projectInuseDetail, settingActivePanels, mode = 'management') => {
    const isDisplayQA = isDisplayCustomerTicketList(projectInuseDetail);
    if (!isDisplayQA) return null;

    const qaMenu = buildBasePanelItem(
        Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST,
        I18n.get('sidebar-customer_ticket_list'),
        0
    );

    if (settingActivePanels) {
        const settingQAPanel = getSettingPanel(settingActivePanels, Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST);
        if (!Utils.isEmptyObject(settingQAPanel)) {
            qaMenu.active = settingQAPanel.active;
            qaMenu.order = settingQAPanel.order;
            qaMenu.checkedAt = settingQAPanel.checkedAt;
        }
    }

    return qaMenu;
};

/**
 * Build Helpdesk panel menu
 */
const buildHelpdeskPanel = (projectInuseDetail, settingActivePanels, mode = 'management') => {
    const isDisplayHelpdesk = isDisplayHelpdeskTicketList(projectInuseDetail);
    if (!isDisplayHelpdesk) return null;

    const helpdeskMenu = buildBasePanelItem(
        Constant.PANEL_TYPE.HELPDESK_TICKET_LIST,
        I18n.get('sidebar-helpdesk_ticket_list'),
        1
    );

    if (settingActivePanels) {
        const settingHelpdeskPanel = getSettingPanel(settingActivePanels, Constant.PANEL_TYPE.HELPDESK_TICKET_LIST);
        if (!Utils.isEmptyObject(settingHelpdeskPanel)) {
            helpdeskMenu.active = settingHelpdeskPanel.active;
            helpdeskMenu.order = settingHelpdeskPanel.order;
            helpdeskMenu.checkedAt = settingHelpdeskPanel.checkedAt;
        }
    }

    return helpdeskMenu;
};

/**
 * Build Knowledge panel menu
 */
const buildKnowledgePanel = async (corporationDetail, projectInuseDetail, settingActivePanels, mode = 'management') => {
    const isDisplayKnowledge = await isDisplayKnowledgeMenu(corporationDetail, projectInuseDetail);
    if (!isDisplayKnowledge) return null;

    const knowledgeMenu = buildBasePanelItem(
        Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST,
        I18n.get('sidebar-knowledge_ticket_list'),
        2
    );

    if (settingActivePanels) {
        const settingKnowledgePanel = getSettingPanel(settingActivePanels, Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST);
        if (!Utils.isEmptyObject(settingKnowledgePanel)) {
            knowledgeMenu.active = settingKnowledgePanel.active;
            knowledgeMenu.order = settingKnowledgePanel.order;
            knowledgeMenu.checkedAt = settingKnowledgePanel.checkedAt;
        }
    }

    return knowledgeMenu;
};

/**
 * Build base panels list
 */
const buildBasePanels = async (corporationDetail, projectInuseDetail, settingActivePanels, mode = 'management') => {
    const basePanels = [];

    // Build QA Panel
    const qaPanel = buildQAPanel(projectInuseDetail, settingActivePanels, mode);
    if (qaPanel) basePanels.push(qaPanel);

    // Build Helpdesk Panel
    const helpdeskPanel = buildHelpdeskPanel(projectInuseDetail, settingActivePanels, mode);
    if (helpdeskPanel) basePanels.push(helpdeskPanel);

    // Build Knowledge Panel
    const knowledgePanel = await buildKnowledgePanel(
        corporationDetail,
        projectInuseDetail,
        settingActivePanels,
        mode
    );
    if (knowledgePanel) basePanels.push(knowledgePanel);

    return basePanels.sort((a, b) => a.order - b.order);
};

/**
 * Build business apps list
 */
const buildBusinessApps = async (projectInuseDetail, settingActivePanels, mode = 'management') => {
    const LIMIT_DISPLAY_ON_SIDEBAR_APPS = 10;
    let businessApps = [];

    let requestParams = {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_MANAGEMENT_BUSINESS_APP_LIST,
        corporationInuseId: localStorageUtil.getAffiliationOrganization(),
        projectInuseId: localStorageUtil.getSelectedProjectId(),
        userId: localStorageUtil.getUserDetailId(),
        isGetDetail: mode === 'management'
    };

    const response = await getPublicAPI(null, requestParams);
    const managementBusinessAppList = Utils.convertJSONDataToObject(response.payload);
    // step 1: define app obj
    if (!Utils.isEmptyArray(managementBusinessAppList)) {
        businessApps = managementBusinessAppList.map(app => {
            let favicon = app?.appFavicon
            if (mode === 'menu' && !Utils.isEmptyString(favicon)) {
                favicon = Utils.getCloudFrontUrlOfFilePublicAll(`/${favicon}`);
            }
            return {
                id: app?.id,
                name: app?.appName,
                affiliationOrganization: app?.affiliationOrganization,
                createdProjectName: app?.createdProjectName || '',
                affiliationProject: app?.affiliationProject,
                favicon: favicon,
                appSrc: app?.appSrc,
                order: null,
                active: false,
                checkedAt: null,
                createdAt: app?.createdAt,
            }
        })

        // Use common function to sort and assign order
        businessApps = sortAndAssignOrder(businessApps);
    }

    if (
        [Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(projectInuseDetail.type) &&
        !Utils.isEmptyArray(businessApps)
    ) {
        businessApps = businessApps
            .map(app => ({
                ...app,
                active: true
            }))
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        return mode === 'menu' ? businessApps.slice(0, LIMIT_DISPLAY_ON_SIDEBAR_APPS) : businessApps
    }

    // handle delete app in settingActivePanels => todo if not exist in businessApps, then delete from settingActivePanels
    if (settingActivePanels && !Utils.isEmptyArray(businessApps)) {
        businessApps = buildBusinessAppsByProjectInuseSetting(businessApps, settingActivePanels)
    }

    return mode === 'menu'
        ? businessApps.filter(app => app.active).slice(0, LIMIT_DISPLAY_ON_SIDEBAR_APPS)
        : businessApps;
};

/**
 * Main initialization function
 */
const initManagementPanels = async (corporationDetail, projectInuseDetail, setManagementPanel) => {
    const settingManagementPanel = Utils.convertJSONDataToObject(projectInuseDetail.managementPanel);
    const settingActivePanels = settingManagementPanel?.activePanel;

    const [basePanels, businessApps] = await Promise.all([
        buildBasePanels(
            corporationDetail,
            projectInuseDetail,
            settingActivePanels,
            'management'
        ),
        buildBusinessApps(projectInuseDetail, settingActivePanels, 'management')
    ]);

    setManagementPanel({
        basePanel: basePanels,
        businessApp: businessApps
    });
};

const initManagementPanelMenus = async (corporationDetail, projectInuseDetail, setManagementMenu) => {
    const settingManagementPanel = Utils.convertJSONDataToObject(projectInuseDetail.managementPanel);
    const settingActivePanels = settingManagementPanel.activePanel;

    const [basePanels, businessApps] = await Promise.all([
        buildBasePanels(
            corporationDetail,
            projectInuseDetail,
            settingActivePanels,
            'menu'
        ),
        buildBusinessApps(projectInuseDetail, settingActivePanels, 'menu')
    ]);

    setManagementMenu({
        basePanel: basePanels,
        businessApp: businessApps
    });
};

const filterPanelTabByManagementPanel = async (corporationDetail, projectInuseDetail, ticketAppListId = []) => {
    const settingManagementPanel = Utils.convertJSONDataToObject(projectInuseDetail.managementPanel);
    const settingActivePanels = settingManagementPanel.activePanel;
    const modeMenu = 'menu'

    // Build QA Panel
    const qaPanel = buildQAPanel(projectInuseDetail, settingActivePanels, modeMenu);

    // Build Helpdesk Panel
    const helpdeskPanel = buildHelpdeskPanel(projectInuseDetail, settingActivePanels, modeMenu);

    // Build Knowledge Panel
    const knowledgePanel = await buildKnowledgePanel(
        corporationDetail,
        projectInuseDetail,
        settingActivePanels,
        modeMenu
    );

    let listActiveAppDetail = [];

    if (!Utils.isEmptyArray(ticketAppListId)) {
        // handle filter can not use ticketAppListId ...
        const listAppCanView = await buildBusinessApps(projectInuseDetail, settingActivePanels, modeMenu);

        for (let app of listAppCanView) {
            if (!app || Utils.isEmptyObject(app)) continue;
            if (ticketAppListId.includes(app.id)) {
                listActiveAppDetail.push(app);
            }
        }
    }
    
    return {
        isFilterQA: !Boolean(qaPanel?.active),
        isFilterHelpdesk: !Boolean(helpdeskPanel?.active),
        isFilterKnowledge: !Boolean(knowledgePanel?.active),
        listActiveAppDetail: listActiveAppDetail
    }
}

const handleSubmitManagementPanel = async (managementPanel, args) => {
    const {
        corporationDetail,
        projectInuseDetail,
        userDetail,
        enqueueSnackbar,
        setIsLoading,
        setIsShowPopUpReload
    } = args;
    const updateProjectProfileParams = {
        method: Constant.UPDATE_PROJECT_RESOURCE_METHOD.UPDATE_MANAGEMENT_PANEL,
        corporationId: corporationDetail.id || localStorageUtil.getAffiliationOrganization(),
        userId: userDetail?.id || localStorageUtil.getUserDetailId(),
        projectId: projectInuseDetail.id || localStorageUtil.getSelectedProjectId(),
        managementPanel: JSON.stringify(managementPanel),
    }

    const updatedProject = await handleUpdateProjectResource(null, updateProjectProfileParams);

    setIsLoading(false);

    const { status } = updatedProject;
    if (status) {
        enqueueSnackbar(buildSnackbarMessage('COMMSG047'), {
            variant: 'success',
            autoHideDuration: 3000
        });
    } else {
        setIsShowPopUpReload(true);
    }
}

const buildShareLinkBusinessApp = (appSrc) => {
    if (!appSrc) return '';
    // appSrc: "public/assets/html/corp_xxx/ticket/knowledge_xxx/field_xxx.html"
    const paths = appSrc.split('/');

    const ticketIndex = paths.findIndex(p => p === 'ticket');
    if (ticketIndex === -1 || !paths[ticketIndex + 1] || !paths[ticketIndex + 2]) return '';

    const ticketId = paths[ticketIndex + 1];
    const fieldFile = paths[ticketIndex + 2];

    const fieldId = fieldFile.replace('.html', '');

    return `${window.location.origin}/share/app/${ticketId}/${fieldId}`;
};

export {
    filterPanelTabByManagementPanel,
    initManagementPanels,
    initManagementPanelMenus,
    handleSubmitManagementPanel,
    buildShareLinkBusinessApp,
    buildBusinessApps
}
